import React from "react";
import { 
    Box, 
    Typography, 
    Button, 
    Grid, 
    Card, 
    CardContent, 
    useTheme,
    Container,
    Stack,
    Divider
} from "@mui/material";
import { tokens } from "../../theme";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Link } from "react-router-dom";
import curtainWallingIcon from "../../images/icons/curtain-walling-installation.png";
import claddingIcon from "../../images/icons/cladding-installation.png";
import remediationIcon from "../../images/icons/fire-remediation.png";

const About = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const stats = [
        { prefix: "Established in", highlight: "2018" },
        { number: "100%", label: "Client Satisfaction" },
        { number: "30+", label: "Team Members" }
    ];

    const whoWeAreParagraphs = [
        "Established in 2018, AV Facade Services Ltd is a specialist labour-only facade installation contractor delivering high-quality facade solutions across the UK. We have built a strong reputation by working alongside leading main contractors, successfully completing projects across the commercial, residential, education, healthcare, and mixed-use sectors.",
        "Our expertise covers the installation of curtain walling, aluminium windows and doors, cladding, and facade remedial works. From large-scale new-build developments to complex refurbishment projects, we provide skilled installation teams committed to delivering every project safely, efficiently, and to the highest standard.",
        "At AV Facade Services, we understand that every project is unique. That's why we work closely with our clients, adapting to programme requirements while maintaining exceptional attention to detail, quality, and compliance with industry standards. Our experienced operatives are dedicated to achieving outstanding results, ensuring installations are completed with precision, reliability, and professionalism.",
        "Since our establishment, we have successfully delivered numerous façade projects across the UK, earning the trust of contractors through our consistent workmanship, strong work ethic, and commitment to health and safety. We take pride in building lasting relationships with our clients by delivering dependable labour solutions that keep projects moving forward.",
        "As our business continues to grow, our focus remains unchanged: providing expert façade installation services backed by skilled professionals, transparent communication, and an unwavering commitment to quality on every project.",
    ];

    const services = [
        {
            icon: curtainWallingIcon,
            title: "Curtain wall",
            description: "Key Services",
            features: [
                "Schüco, Kawneer, Technal, Metal Technology, SAS, Aluprof & SAPA systems",
                "Stick and unitised curtain walling",
                "New-build and refurbishment projects",
                "Precision installation and quality assurance"
            ]
        },
        {
            icon: claddingIcon,
            title: "Cladding",
            description: "Key Services",
            features: [
                "Brick Slip, Terracotta & Hardie® Plank systems",
                "Ash & Lacy, Trespa, Parklex, Euroclad & Eurobond systems",
                "New-build and refurbishment projects",
                "Precision installation and quality assurance"
            ]
        },
        {
            icon: remediationIcon,
            title: "Remediation",
            description: "Key Services",
            features: [
                "Façade remediation & replacement works",
                "Cladding, glazing & façade component replacement",
                "EPDM, insulation & breather membrane installation",
                "Building safety compliance & quality assurance"
            ]
        }
    ];

    return (
        <Box>
            {/* Hero Section */}
            <Box
                sx={{
                    background: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1920')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundAttachment: "fixed",
                    py: { xs: 8, md: 12 },
                    textAlign: "center",
                    color: "white"
                }}
            >
                <Container maxWidth="lg">
                    <Typography 
                        variant="h1" 
                        sx={{ 
                            mb: 2,
                            fontSize: { xs: '2.5rem', md: '3.5rem' },
                            fontWeight: 'bold'
                        }}
                    >
                        About Us
                    </Typography>
                    <Typography 
                        variant="h4" 
                        sx={{ 
                            mb: 4,
                            fontSize: { xs: '1.2rem', md: '1.5rem' },
                            color: theme.palette.common.white
                        }}
                    >
                        Building Excellence. Delivering Precision. Earning Trust.
                    </Typography>

                    {/* Stats Section */}
                    <Grid container spacing={4} sx={{ mt: 4 }} justifyContent="center" alignItems="stretch">
                        {stats.map((stat, index) => (
                            <Grid item xs={12} sm={4} key={index} sx={{ display: 'flex' }}>
                                <Box 
                                    sx={{ 
                                        p: 2,
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: 100,
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        borderRadius: 2,
                                        backdropFilter: 'blur(10px)'
                                    }}
                                >
                                    {stat.prefix ? (
                                        <>
                                            <Typography
                                                variant="h3"
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                    fontWeight: 'bold',
                                                    mb: 1,
                                                }}
                                            >
                                                {stat.highlight}
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: theme.palette.common.white }}>
                                                {stat.prefix}
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <Typography 
                                                variant="h3" 
                                                sx={{ 
                                                    color: theme.palette.primary.main,
                                                    fontWeight: 'bold',
                                                    mb: 1
                                                }}
                                            >
                                                {stat.number}
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: theme.palette.common.white }}>
                                                {stat.label}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Main Content */}
            <Box sx={{ backgroundColor: colors.background[200], py: 8 }}>
                <Container maxWidth="lg">
                    {/* Services Section */}
                    <Grid container spacing={4} sx={{ mb: 8 }}>
                        {services.map((service, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Card 
                                    sx={{ 
                                        height: '100%',
                                        backgroundColor: colors.background[300],
                                        transition: 'transform 0.3s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-8px)'
                                        }
                                    }}
                                >
                                    <CardContent>
                                        <Stack spacing={3}>
                                            <Box sx={{ textAlign: 'center', mb: 2 }}>
                                                {/* The illustrations are navy line art on an opaque white
                                                    background, so they sit on a white circular chip to read
                                                    as a badge on the dark card rather than a white square. */}
                                                <Box
                                                    component="img"
                                                    src={service.icon}
                                                    alt=""
                                                    aria-hidden="true"
                                                    sx={{
                                                        display: 'block',
                                                        mx: 'auto',
                                                        width: { xs: 64, md: 76 },
                                                        height: { xs: 64, md: 76 },
                                                        borderRadius: '50%',
                                                        backgroundColor: '#ffffff'
                                                    }}
                                                />
                                                <Typography 
                                                    variant="h5" 
                                                    sx={{ 
                                                        mt: 2,
                                                        color: theme.palette.text.primary,
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {service.title}
                                                </Typography>
                                            </Box>
                                            
                                            <Typography 
                                                variant="body1" 
                                                sx={{ 
                                                    color: theme.palette.text.secondary,
                                                    textAlign: 'center'
                                                }}
                                            >
                                                {service.description}
                                            </Typography>

                                            <Divider sx={{ borderColor: theme.palette.divider }} />

                                            <Stack spacing={1}>
                                                {service.features.map((feature, idx) => (
                                                    <Box 
                                                        key={idx} 
                                                        sx={{ 
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1
                                                        }}
                                                    >
                                                        <CheckCircleIcon 
                                                            sx={{ 
                                                                color: theme.palette.primary.main,
                                                                fontSize: 20
                                                            }} 
                                                        />
                                                        <Typography 
                                                            variant="body2"
                                                            sx={{ color: theme.palette.text.secondary }}
                                                        >
                                                            {feature}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Who We Are Section */}
                    <Box sx={{ mb: 8, textAlign: 'center' }}>
                        <Typography 
                            variant="h2" 
                            sx={{ 
                                mb: 3,
                                color: theme.palette.text.primary
                            }}
                        >
                            Who We Are
                        </Typography>
                        <Stack spacing={3} sx={{ maxWidth: '800px', margin: '0 auto' }}>
                            {whoWeAreParagraphs.map((paragraph, index) => (
                                <Typography 
                                    key={index}
                                    variant="body1" 
                                    sx={{ 
                                        fontSize: '1.1rem',
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.8,
                                        textAlign: 'left'
                                    }}
                                >
                                    {paragraph}
                                </Typography>
                            ))}
                        </Stack>
                        <Box
                            sx={{
                                mt: 5,
                                mx: 'auto',
                                maxWidth: '700px',
                                px: 4,
                                py: 3,
                                borderLeft: `4px solid ${theme.palette.primary.main}`,
                                backgroundColor: colors.background[300],
                                borderRadius: 1,
                                textAlign: 'left',
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontStyle: 'italic',
                                    color: theme.palette.text.primary,
                                    lineHeight: 1.7,
                                }}
                            >
                                "Our reputation is built on precision, reliability, and delivering quality facade installations that stand the test of time."
                            </Typography>
                        </Box>
                    </Box>

                    {/* CTA Section */}
                    <Box
                        sx={{
                            mt: 8,
                            p: 6,
                            textAlign: 'center',
                            backgroundColor: theme.palette.primary.main,
                            borderRadius: 2,
                            position: 'relative',
                            overflow: 'hidden',
                            color: theme.palette.common.white
                        }}
                    >
                        <Typography 
                            variant="h3" 
                            sx={{ 
                                mb: 2,
                                fontWeight: 'bold',
                                color: 'inherit'
                            }}
                        >
                            Ready to Start Your Project?
                        </Typography>
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                mb: 4,
                                color: 'rgba(255, 255, 255, 0.9)',
                                maxWidth: '600px',
                                mx: 'auto'
                            }}
                        >
                            Contact us today for a free consultation and estimate.
                        </Typography>
                        <Button
                            component={Link}
                            to="/contact"
                            variant="contained"
                            size="large"
                            onClick={() => window.scrollTo(0, 0)}
                            sx={{
                                py: 2,
                                px: 4,
                                backgroundColor: theme.palette.common.white,
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                                },
                                fontSize: '1.1rem'
                            }}
                        >
                            Contact Us
                        </Button>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default About;
